import { create } from 'zustand'

import { get as apiGet } from '@/api/request'
import type { ReferralLevel, DirectReferral, Pagination, ReferralStats } from '@/types/api'

interface ReferralStore {
  referralCode: string | null
  stats: ReferralStats | null
  levels: ReferralLevel[]
  referrals: DirectReferral[]
  currentLevel: number
  pagination: (Pagination & { hasMore: boolean }) | null
  isLoading: boolean
  isReferralsLoading: boolean
  error: string | null
  _initialized: boolean
  fetch: (opts?: { force?: boolean }) => Promise<void>
  setCurrentLevel: (level: number) => void
  loadMore: () => Promise<void>
  reset: () => void
}

const LIMIT = 10

export const useReferralStore = create<ReferralStore>((set, get) => ({
  referralCode: null,
  stats: null,
  levels: [],
  referrals: [],
  currentLevel: 1,
  pagination: null,
  isLoading: false,
  isReferralsLoading: false,
  error: null,
  _initialized: false,

  async fetch(opts?: { force?: boolean }) {
    const force = opts?.force ?? false
    if (!get()._initialized || force) {
      set({ isLoading: true, error: null })
    }
    try {
      const res = await apiGet<{
        referralCode: string
        stats: ReferralStats
        levels: ReferralLevel[]
      }>('/users/referrals')
      if (res.data?.data) {
        set({
          referralCode: res.data.data.referralCode,
          stats: res.data.data.stats,
          levels: res.data.data.levels,
          error: null,
        })
      }
    } catch (err) {
      if (!get()._initialized || force) {
        set({ error: err instanceof Error ? err.message : 'Failed' })
      }
    }
    try {
      set({ isReferralsLoading: true })
      const listRes = await apiGet<{ referrals: DirectReferral[]; pagination: Pagination }>(
        '/users/referrals/list',
        { params: { level: get().currentLevel, page: 1, limit: LIMIT } },
      )
      if (listRes.data?.data) {
        const pg = listRes.data.data.pagination
        set({
          referrals: listRes.data.data.referrals,
          pagination: pg ? { ...pg, hasMore: pg.page < pg.pages } : null,
        })
      }
    } catch {
      /* non-critical */
    } finally {
      set({ isLoading: false, isReferralsLoading: false, _initialized: true })
    }
  },

  setCurrentLevel(level) {
    if (level === get().currentLevel) {
      return
    }
    set({ currentLevel: level, referrals: [], pagination: null })
    ;(async () => {
      set({ isReferralsLoading: true })
      try {
        const res = await apiGet<{ referrals: DirectReferral[]; pagination: Pagination }>(
          '/users/referrals/list',
          { params: { level, page: 1, limit: LIMIT } },
        )
        if (res.data?.data) {
          const pg = res.data.data.pagination
          set({
            referrals: res.data.data.referrals,
            pagination: pg ? { ...pg, hasMore: pg.page < pg.pages } : null,
          })
        }
      } catch {
        /* non-critical */
      } finally {
        set({ isReferralsLoading: false })
      }
    })()
  },

  async loadMore() {
    const { pagination, currentLevel, referrals, isReferralsLoading } = get()
    if (isReferralsLoading || !pagination?.hasMore) {
      return
    }
    set({ isReferralsLoading: true })
    try {
      const next = pagination.page + 1
      const res = await apiGet<{ referrals: DirectReferral[]; pagination: Pagination }>(
        '/users/referrals/list',
        { params: { level: currentLevel, page: next, limit: LIMIT } },
      )
      if (res.data?.data) {
        const pg = res.data.data.pagination
        set({
          referrals: [...referrals, ...res.data.data.referrals],
          pagination: pg ? { ...pg, hasMore: pg.page < pg.pages } : null,
        })
      }
    } catch {
      /* non-critical */
    } finally {
      set({ isReferralsLoading: false })
    }
  },

  reset() {
    set({
      referralCode: null,
      stats: null,
      levels: [],
      referrals: [],
      currentLevel: 1,
      pagination: null,
      isLoading: false,
      isReferralsLoading: false,
      error: null,
      _initialized: false,
    })
  },
}))
